import { Button } from '@rocco/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import { useAcceptQuote } from '../../hooks/useAcceptQuote';
import { LoaderCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { SignatureCanvas } from '~/modules/signature/components/SignatureCanvas';
import { useAtom } from 'jotai';
import {
    signatureDataAtom,
    signatureModeAtom,
    signatureFontFamilyAtom,
} from '~/modules/ecommerce/states/accept-quote-popup-atoms';
import { z } from 'zod';

// Validation schema for the accept quote form
const acceptQuoteSchema = z.object({
    fullname: z
        .string()
        .min(3, 'Full name must be at least 3 characters')
        .max(100, 'Full name must be no more than 100 characters')
        .trim(),
    email: z
        .string()
        .email('Please enter a valid email address')
        .min(8, 'Email must be at least 8 characters')
        .max(100, 'Email must be no more than 100 characters')
        .trim(),
    signature: z.string().min(1, 'Signature is required'),
    pixelCount: z.number().min(200, 'Signature is not detailed enough'),
    hasSignature: z
        .boolean()
        .refine((val) => val === true, 'Signature is required'),
});

interface AcceptQuotePopupProps {
    onConfirm: (signatureData: {
        name: string;
        email: string;
        signatureDataUrl: string;
        pixelCount: number;
        signedAt: string;
    }) => void;
    isLoading?: boolean;
}

/**
 * Accept quote confirmation popup component with signature functionality
 * Displays a confirmation dialog when user wants to accept a quote
 * Requires full name, email, and signature from the user
 */
export const AcceptQuotePopup = ({
    onConfirm,
    isLoading = false,
}: AcceptQuotePopupProps) => {
    const { isAcceptQuotePopupOpen, closePopup, customerName, customerEmail } =
        useAcceptQuote();

    // Form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Use atoms for signature data and settings
    const [signatureData, setSignatureData] = useAtom(signatureDataAtom);
    const [, setSignatureMode] = useAtom(signatureModeAtom);
    const [, setSignatureFontFamily] = useAtom(signatureFontFamilyAtom);

    // Set default values when popup opens
    useEffect(() => {
        if (isAcceptQuotePopupOpen) {
            setName(customerName);
            setEmail(customerEmail);
            // Clear signature data and reset signature settings when popup opens
            setSignatureData({
                hasSignature: false,
                dataUrl: '',
                pixelCount: 0,
            });
            setSignatureMode(false); // Reset to typed mode
            setSignatureFontFamily("'Allura', cursive"); // Reset to default font
        }
    }, [
        isAcceptQuotePopupOpen,
        customerName,
        customerEmail,
        setSignatureData,
        setSignatureMode,
        setSignatureFontFamily,
    ]);

    const handleConfirm = async () => {
        setError(null);

        // Validate form data using Zod
        const formData = {
            fullname: name,
            email: email,
            signature: signatureData.dataUrl,
            pixelCount: signatureData.pixelCount,
            hasSignature: signatureData.hasSignature,
        };

        const validationResult = acceptQuoteSchema.safeParse(formData);

        if (!validationResult.success) {
            // Get the first validation error message
            const firstError = validationResult.error.errors[0];
            setError(firstError?.message || 'Validation failed');
            return;
        }

        try {
            const signatureDataForSubmit = {
                name: validationResult.data.fullname,
                email: validationResult.data.email,
                signatureDataUrl: validationResult.data.signature,
                pixelCount: validationResult.data.pixelCount,
                signedAt: new Date().toISOString(),
            };

            onConfirm(signatureDataForSubmit);
            closePopup();

            // Reset form
            setName('');
            setEmail('');
        } catch (err) {
            console.error(err);
            setError('Something went wrong. Please try again.');
        }
    };

    const handleCancel = () => {
        // Reset form
        setName('');
        setEmail('');
        setError(null);
        closePopup();
    };

    return (
        <Dialog
            open={isAcceptQuotePopupOpen}
            onOpenChange={(open) => {
                if (!open) {
                    handleCancel();
                }
            }}
        >
            <DialogContent
                className="max-h-[90vh] w-full max-w-2xl overflow-y-auto"
                closeButton={false}
            >
                <DialogTitle className="sr-only">
                    Sign to Accept Quote
                </DialogTitle>
                <DialogDescription className="sr-only">
                    Please provide your details and signature to accept this
                    quote.
                </DialogDescription>
                <div className="p-2">
                    <div className="mb-4">
                        <h2 className="mb-2 text-2xl font-semibold text-[#141414]">
                            Sign to Accept Quote
                        </h2>
                        <p className="text-[#141414]">
                            Please provide your details and signature to accept
                            this quote.
                        </p>
                    </div>

                    {/* Form Fields */}
                    <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-[#141414]">
                                Full name *
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Jane Doe"
                                className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-3 text-[#141414] placeholder:text-gray-400"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-[#141414]">
                                Email *
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="jane@archipro.co.nz"
                                className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-3 text-[#141414] placeholder:text-gray-400"
                            />
                        </div>
                    </div>

                    {/* Signature Canvas */}
                    <SignatureCanvas
                        name={name}
                        isVisible={isAcceptQuotePopupOpen}
                        canvasHeight={180}
                        canvasMaxWidth={600}
                        onlySignatureFamily="'Allura', cursive"
                    />

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                        <Button
                            color="outline"
                            onClick={handleCancel}
                            disabled={isLoading}
                            className="h-12 px-6"
                        >
                            <span className="font-medium text-[#141414]">
                                Cancel
                            </span>
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={isLoading}
                            className="h-12 px-6"
                        >
                            <span className="font-medium text-white">
                                {isLoading ? (
                                    <>
                                        <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                        Accepting...
                                    </>
                                ) : (
                                    'Accept Quote'
                                )}
                            </span>
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
